import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axiosInstance, { clearAuthData, isJwtAuthFailure } from "../../components/content/axiosConfig";
import Modal from "react-modal";
import Swal from 'sweetalert2';
import Preloader from "../../components/custom/preLoader";
import ListPagination from "../../components/custom/ListPagination";
import useServerPaginatedList from "../../hooks/useServerPaginatedList";
import showErrorToast, { showSuccessToast } from "../../components/toast/toast";

const PondNote = () => {
  const [ponds, setPonds] = useState([]);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalData, setModalData] = useState({
    Note: "",
    PondID: ""
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);

  const fetchPondNotesPage = useCallback(async ({ search, page, limit }) => {
    try {
      return await axiosInstance.get(`/api/PondNote/GetAllNotes`, {
        params: { search, page, limit },
      });
    } catch (error) {
      if (isJwtAuthFailure(error)) {
        clearAuthData();
        window.location.href = "/user/login";
        return { data: { data: [], total: 0 } };
      }
      showErrorToast(error.response?.data?.message || "An error occurred.");
      throw error;
    }
  }, []);

  const {
    rows: pondNotes,
    totalCount,
    searchTerm,
    setSearchTerm,
    currentPage,
    setCurrentPage,
    isLoading,
    entriesPerPage,
    refetch,
  } = useServerPaginatedList(fetchPondNotesPage);

  useEffect(() => {
    axiosInstance.get(`api/Pond/GetAllPonds`, { params: { search: '', page: 1, limit: 500 } })
      .then((response) => {
        const body = response.data;
        setPonds(Array.isArray(body) ? body : body?.data || []);
      })
      .catch((error) => {
        if (isJwtAuthFailure(error)) {
          clearAuthData();
          window.location.href = "/user/login";
          return;
        }
        showErrorToast(error.response?.data?.message || "Failed to fetch ponds");
      });
  }, []);

  const DeletePondNote = async (noteId) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
    });

    if (result.isConfirmed) {
      try {
        setIsSubmitting(true);
        await axiosInstance.delete(`/api/PondNote/DeleteNote/${noteId}`);
        await refetch();
        showSuccessToast("Pond note deleted successfully");
      } catch (error) {
        if (isJwtAuthFailure(error)) {
          clearAuthData();
          window.location.href = "/user/login";
          return;
        }
        showErrorToast(error.response?.data?.message || "An error occurred.");
        console.error("Error deleting pond note:", error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const openModal = (data = null) => {
    if (data) {
      setIsEditing(true);
      setEditId(data.PondNoteID);
      setModalData({
        Note: data.PondNote,
        PondID: data.PondID,
      });
    } else {
      setIsEditing(false);
      setEditId(null);
      setModalData({
        Note: "",
        PondID: "",
      });
    }
    setModalIsOpen(true);
  };

  const closeModal = () => {
    setModalIsOpen(false);
  };

  const handleChange = (e) => {
    const { id, value } = e.target;
    setModalData({
      ...modalData,
      [id]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      if (isEditing) {
        await axiosInstance.put(`/api/PondNote/UpdateNote/${editId}`, {
          ...modalData,
          PondID: Number(modalData.PondID)
        });
        showSuccessToast("Pond note updated successfully");
      } else {
        await axiosInstance.post(`/api/PondNote/AddNote`, {
          ...modalData,
          PondID: Number(modalData.PondID)
        });
        showSuccessToast("Pond note added successfully");
      }
      await refetch();
      closeModal();
    } catch (error) {
      if (isJwtAuthFailure(error)) {
        clearAuthData();
        window.location.href = "/user/login";
        return;
      }
      showErrorToast(error.response?.data?.message || "An error occurred.");
      console.error("Error submitting pond note:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading && pondNotes.length === 0) {
    return <Preloader />;
  }

  return (
    <div>
      <div className="d-flex align-items-center mb-3">
        <div>
          <ul className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-item active">
              <Link to="/pondNotes">Pond Notes</Link>
            </li>
          </ul>
          <h1 className="page-header mb-0">Pond Notes</h1>
        </div>
        <div className="ms-auto">
          <button
            className="btn btn-success btn-rounded px-4 rounded-pill"
            onClick={() => openModal()}
          >
            <i className="fa fa-plus fa-lg me-2 ms-n2 text-success-900"></i>
            Add Pond Note
          </button>
        </div>
      </div>
      <div className="card border-0">
        <div className="tab-content p-3">
          <div className="tab-pane fade show active" id="allTab">
            <div className="input-group mb-3">
              <div className="flex-fill position-relative">
                <div className="input-group">
                  <div
                    className="input-group-text position-absolute top-0 bottom-0 bg-none border-0 start-0"
                    style={{ zIndex: 10 }}
                  >
                    <i className="fa fa-search opacity-5"></i>
                  </div>
                  <input
                    type="text"
                    className="form-control px-35px bg-light"
                    placeholder="Search Pond..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
            </div>
            <div className="table-responsive mb-3">
              <table className="table table-hover table-panel text-nowrap align-middle mb-0">
                <thead>
                  <tr>
                    <th>Pond Name</th>
                    <th>Note</th>
                    <th>Updated At</th>
                    <th>By Staff</th>
                    <th>Account</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan={6} className="text-center text-muted py-5">
                        Loading…
                      </td>
                    </tr>
                  ) : (
                    pondNotes.map((pondNote) => (
                      <tr key={pondNote.PondNoteID}>
                        <td>{pondNote.PondName}</td>
                        <td>{pondNote.PondNote}</td>
                        <td>{pondNote.UpdatedAt}</td>
                        <td>{pondNote.StaffName}</td>
                        <td>{pondNote.FisherName}</td>
                        <td>
                          <span onClick={() => openModal(pondNote)}>
                            <i className="fa fa-pencil fs-13px fa-fw me-5px"></i>
                          </span>
                          &nbsp;&nbsp;
                          <span>
                            <i
                              onClick={() => DeletePondNote(pondNote.PondNoteID)}
                              className="fa fa-trash fs-13px fa-fw me-5px cursor-pointer"
                            ></i>
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <ListPagination
              currentPage={currentPage}
              totalCount={totalCount}
              entriesPerPage={entriesPerPage}
              onPageChange={setCurrentPage}
            />
          </div>
        </div>
      </div>

      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        style={{
          content: {
            width: "800px",
            height: "400px",
            margin: "auto",
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -20%)",
            padding: "50px",
          },
          overlay: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
          },
        }}
      >
        <form onSubmit={handleSubmit} className="fs-13px">
          <div className="d-flex align-items-center mb-3">
            <div>
              <h2>{isEditing ? "Edit Pond Note" : "Add Pond Note"}</h2>
            </div>
            <div className="ms-auto">
              <button
                type="submit"
                className="btn btn-success btn-rounded px-4 rounded-pill"
                disabled={isSubmitting}
              >
                <i className={`fa ${isEditing ? 'fa-check' : 'fa-plus'} fa-lg me-2 ms-n2 text-success-900`}></i>
                Submit
              </button>
            </div>
          </div>
          <div className="card border-0">
            <div className="tab-content p-3">
              <div className="tab-pane fade show active" id="allTab">
                <div className="form-floating mb-25px">
                  <select
                    className="form-control h-50px fs-13px"
                    id="PondID"
                    name="PondID"
                    value={modalData.PondID}
                    onChange={handleChange}
                  >
                    <option value="">Select Pond</option>
                    {ponds.map((pond) => (
                      <option key={pond.id} value={pond.id}>{pond.Name}</option>
                    ))}
                  </select>
                  <label
                    htmlFor="PondID"
                    className="d-flex align-items-center fs-13px text-gray-600"
                  >
                    Pond &nbsp;<span>*</span>
                  </label>
                </div>

                <div className="mb-15px">
                  <label
                    htmlFor="Note"
                    className="d-flex align-items-center fs-13px text-gray-600 mb-1"
                  >
                    Note &nbsp;<span>*</span>
                  </label>
                  <textarea
                    className="form-control fs-13px"
                    placeholder="Note"
                    id="Note"
                    name="Note"
                    value={modalData.Note}
                    onChange={handleChange}
                    rows={5}
                    style={{ resize: "vertical", minHeight: "7.5rem" }}
                  />
                </div>
              </div>
            </div>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default PondNote;
